import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

import axios from '../../../api/axios';
import { validateEmail } from '../../../utils/Utils';

interface Candidate {
  id?: number;
  name: string;
  email?: string;
  token?: string;
  [key: string]: any;
}

interface SendGuideModalProps {
  show: boolean;
  onHide: () => void;
  candidate: Candidate | null;
  onSend: (emailAddress: string, urlToken: string) => Promise<boolean>;
}

const SendGuideModal: React.FC<SendGuideModalProps> = ({
  show,
  onHide,
  candidate,
  onSend,
}) => {
  const [localEmailAddress, setLocalEmailAddress] = useState<string>('');
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [urlToken, setUrlToken] = useState<string>('');
  const [candidateUrl, setCandidateUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const modalOpenedRef = useRef<boolean>(false);

  useEffect(() => {
    const generateToken = async () => {
      if (candidate?.id) {
        try {
          setLoading(true);
          setError('');

          const response = await axios.post(`/candidates/${candidate.id}/generate-token`);

          if (response.data) {
            setCandidateUrl(response.data.candidate_url);
            setUrlToken(response.data.token);
            console.log(`Retrieved token for candidate ${candidate.id}: ${response.data.token}`);
          }

          setLocalEmailAddress(candidate?.email ?? '');
          setButtonDisabled(false);
        } catch (error) {
          console.error('Error generate token:', error);
          alert('案内メールURLの生成中にエラーが発生しました。');
          setButtonDisabled(true);
        } finally {
          setLoading(false);
        }
      }
    };

    if (show && candidate && candidate.id && !modalOpenedRef.current) {
      generateToken();
      modalOpenedRef.current = true;
    } else if (!show && modalOpenedRef.current) {
      modalOpenedRef.current = false;
    }
  }, [show, candidate]);

  useEffect(() => {
    if (!show) {
      setError('');
      setLoading(false);
      setCandidateUrl('');
      setButtonDisabled(true);
      setSendingEmail(false);
    }
  }, [show]);

  const handleEmailAddressChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setLocalEmailAddress(value);
  };

  const handleSend = async (): Promise<void> => {
    setSendingEmail(true);
    setButtonDisabled(true);
    await onSend(localEmailAddress, urlToken);
    setSendingEmail(false);
    setButtonDisabled(false);
  };

  const handleModalClose = () => {
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      className="send-guide-modal"
      backdrop="static"  // Prevent close when clicking outside
      keyboard={false}   // Optional: Prevent closing with ESC key
    >
      <Modal.Header closeButton>
        <Modal.Title>案内を送る</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="guide-email-title">
          {candidate?.name || '〇〇〇〇'}さんへリファレンスチェックの案内を送りましょう
        </p>
        <div className="guide-email-template">
          <p className="guide-email-content">
            {candidate?.name || '〇〇〇〇'}様
          </p>
          <p className="guide-email-content">
            フォロリク運営事務局です。<br />
            選考中の{candidate?.user.name || '企業'}から推薦取得のご案内が届いています。
          </p>
          <p className="guide-email-content">
            【概要】<br />
            期限：{(() => {
              const deadline = new Date();
              deadline.setDate(deadline.getDate() + parseInt(process.env.REACT_APP_DEADLINE_DAYS || '7'));
              const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][deadline.getDay()];
              return `${deadline.getFullYear()}年${deadline.getMonth() + 1}月${deadline.getDate()}日（${dayOfWeek}曜日）`;
            })()}<br />
            回答の人数：2人<br />
            回答者の所要時間：約30分
          </p>
          <p className="guide-email-content">
            【回答者の条件】<br />
            1人目：上司・ 先輩（詳細：現職が難しい場合は、前職・前々職でも構いません）<br />
            2人目：同僚・ 同期（詳細：現職が難しい場合は、前職・前々職でも構いません）
          </p>
          <p className="guide-email-content">
            【手順】<br />
            ご回答者様情報のご入力、ご依頼<br />
            ※ご回答者情報をご入力いただくとご回答者様へ案内メールが送付されます。<br />
            {candidateUrl}<br />
            =========================================
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {candidate && (
          <div className="d-flex justify-content-between align-items-center gap-2" style={{ width: '100%' }}>
            <Form.Control
              type="text"
              value={localEmailAddress}
              onChange={handleEmailAddressChange}
              placeholder={`${candidate.name}さんのメールアドレスを入力`}
              className="email-input"
            />
            <Button 
              variant="success" 
              className="send-button ml-2" 
              onClick={handleSend}
              disabled={!localEmailAddress.trim() || !validateEmail(localEmailAddress) || sendingEmail || buttonDisabled || !candidateUrl}
            >
              {sendingEmail ? '送信中...' : '送信'}
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SendGuideModal;
